import React, { FC, useState, useEffect } from 'react';
import './VisualSurveyEditor.css';
import {
    Button,
    Input,
    Textarea,
    Dropdown,
    Option,
    Switch,
    Text,
    Title3,
    Card,
    tokens,
    Badge,
    Divider,
    Field
} from '@fluentui/react-components';
import {
    Add24Regular,
    Delete24Regular,
    ArrowUp24Regular,
    ArrowDown24Regular,
    Save24Regular
} from '@fluentui/react-icons';

// Survey question types
type QuestionType = 'Rating' | 'SingleValue' | 'Text';

// Question interface
interface SurveyQuestion {
    id: string;
    type: QuestionType;
    text: string;
    isMandatory: boolean;
    answerOptions?: string[];
    maxLength?: number;
}

// Survey interface
interface Survey {
    id: string;
    title: string;
    description: string;
    version: string;
    createdDate: string;
    estimatedDuration: string;
    questions: SurveyQuestion[];
}

interface VisualSurveyEditorProps {
    initialSurvey?: Survey;
    onSave: (survey: Survey) => Promise<void>;
    onCancel: () => void;
    isSaving: boolean;
    className?: string;
}

const VisualSurveyEditor: FC<VisualSurveyEditorProps> = ({
    initialSurvey,
    onSave,
    onCancel,
    isSaving,
    className
}) => {
    const [survey, setSurvey] = useState<Survey>(() => {
        if (initialSurvey) {
            return { ...initialSurvey };
        }
        return {
            id: `s_${new Date().getFullYear()}${(new Date().getMonth() + 1).toString().padStart(2, '0')}_1`,
            title: 'New Survey',
            description: '',
            version: '1.0',
            createdDate: new Date().toISOString().split('T')[0],
            estimatedDuration: '5-10 minutes',
            questions: []
        };
    });

    // Update survey state when initialSurvey changes (e.g., when data loads from API)
    useEffect(() => {
        if (initialSurvey) {
            setSurvey({ ...initialSurvey });
        }
    }, [initialSurvey]);

    const questionTypeOptions: { value: QuestionType; label: string; description: string }[] = [
        { value: 'Rating', label: 'Rating', description: 'Multiple choice with predefined options (radio buttons)' },
        { value: 'SingleValue', label: 'Single Value', description: 'Single selection from options (dropdown/radio)' },
        { value: 'Text', label: 'Text', description: 'Free text input with optional character limit' }
    ];

    const updateSurveyField = (field: keyof Survey, value: any) => {
        setSurvey(prev => ({ ...prev, [field]: value }));
    };

    const addQuestion = () => {
        const newQuestion: SurveyQuestion = {
            id: `q${survey.questions.length + 1}`,
            type: 'Text',
            text: '',
            isMandatory: false
        };
        setSurvey(prev => ({
            ...prev,
            questions: [...prev.questions, newQuestion]
        }));
    };

    const updateQuestion = (index: number, field: keyof SurveyQuestion, value: any) => {
        setSurvey(prev => ({
            ...prev,
            questions: prev.questions.map((q, i) => 
                i === index ? { ...q, [field]: value } : q
            )
        }));
    };

    const deleteQuestion = (index: number) => {
        setSurvey(prev => ({
            ...prev,
            questions: prev.questions.filter((_, i) => i !== index)
        }));
    };

    const moveQuestion = (index: number, direction: 'up' | 'down') => {
        if (
            (direction === 'up' && index === 0) ||
            (direction === 'down' && index === survey.questions.length - 1)
        ) {
            return;
        }

        setSurvey(prev => {
            const newQuestions = [...prev.questions];
            const newIndex = direction === 'up' ? index - 1 : index + 1;
            [newQuestions[index], newQuestions[newIndex]] = [newQuestions[newIndex], newQuestions[index]];
            
            // Update IDs to maintain order
            newQuestions.forEach((q, i) => {
                q.id = `q${i + 1}`;
            });
            
            return { ...prev, questions: newQuestions };
        });
    };

    const addAnswerOption = (questionIndex: number) => {
        updateQuestion(questionIndex, 'answerOptions', [
            ...(survey.questions[questionIndex].answerOptions || []),
            ''
        ]);
    };

    const updateAnswerOption = (questionIndex: number, optionIndex: number, value: string) => {
        const currentOptions = survey.questions[questionIndex].answerOptions || [];
        const newOptions = [...currentOptions];
        newOptions[optionIndex] = value;
        updateQuestion(questionIndex, 'answerOptions', newOptions);
    };

    const deleteAnswerOption = (questionIndex: number, optionIndex: number) => {
        const currentOptions = survey.questions[questionIndex].answerOptions || [];
        const newOptions = currentOptions.filter((_, i) => i !== optionIndex);
        updateQuestion(questionIndex, 'answerOptions', newOptions);
    };

    const handleSave = async () => {
        await onSave(survey);
    };

    const needsAnswerOptions = (type: QuestionType): boolean => {
        return ['Rating', 'SingleValue'].includes(type);
    };

    const needsMaxLength = (type: QuestionType): boolean => {
        return type === 'Text';
    };

    return (
        <div className={`visual-survey-editor ${className || ''}`}>
            {/* Header */}
            <div className="survey-editor-header">
                <Title3>Visual Survey Editor</Title3>
            </div>

            {/* Survey Basic Information */}
            <Card style={{ padding: '20px', marginBottom: '20px' }}>
                <Text size={500} weight="semibold" style={{ marginBottom: '16px' }}>
                    Survey Information
                </Text>
                
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                    <Field label="Survey ID" required>
                        <Input
                            value={survey.id}
                            onChange={(_, data) => updateSurveyField('id', data.value)}
                            placeholder="e.g., s_202508_1"
                        />
                    </Field>
                    
                    <Field label="Version">
                        <Input
                            value={survey.version}
                            readOnly
                            placeholder="e.g., 1.0"
                            style={{ backgroundColor: '#f5f5f5', cursor: 'not-allowed' }}
                        />
                    </Field>
                </div>

                <Field label="Survey Title" required style={{ marginBottom: '16px' }}>
                    <Input
                        value={survey.title}
                        onChange={(_, data) => updateSurveyField('title', data.value)}
                        placeholder="Enter survey title"
                    />
                </Field>

                <Field label="Description" style={{ marginBottom: '16px' }}>
                    <Textarea
                        value={survey.description}
                        onChange={(_, data) => updateSurveyField('description', data.value)}
                        placeholder="Enter survey description"
                        rows={3}
                    />
                </Field>

                <Field label="Estimated Duration">
                    <Input
                        value={survey.estimatedDuration}
                        onChange={(_, data) => updateSurveyField('estimatedDuration', data.value)}
                        placeholder="e.g., 5-10 minutes"
                    />
                </Field>
            </Card>

            {/* Questions Section */}
            <Card style={{ padding: '20px' }}>
                <div style={{ marginBottom: '16px' }}>
                    <Text size={500} weight="semibold">
                        Questions ({survey.questions.length})
                    </Text>
                </div>

                {survey.questions.length === 0 && (
                    <div style={{ textAlign: 'center', padding: '40px', color: tokens.colorNeutralForeground2 }}>
                        <Text>No questions added yet. Use the "Add Question" button below to get started.</Text>
                    </div>
                )}

                {survey.questions.map((question, questionIndex) => (
                    <Card key={question.id} style={{ padding: '16px', marginBottom: '16px', border: `1px solid ${tokens.colorNeutralStroke1}` }}>
                        {/* Question Header */}
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                            <Badge appearance="filled" color="brand">Question {questionIndex + 1}</Badge>
                            <div style={{ display: 'flex', gap: '4px' }}>
                                <Button
                                    size="small"
                                    appearance="subtle"
                                    icon={<ArrowUp24Regular />}
                                    onClick={() => moveQuestion(questionIndex, 'up')}
                                    disabled={questionIndex === 0}
                                />
                                <Button
                                    size="small"
                                    appearance="subtle"
                                    icon={<ArrowDown24Regular />}
                                    onClick={() => moveQuestion(questionIndex, 'down')}
                                    disabled={questionIndex === survey.questions.length - 1}
                                />
                                <Button
                                    size="small"
                                    appearance="subtle"
                                    icon={<Delete24Regular />}
                                    onClick={() => deleteQuestion(questionIndex)}
                                    style={{ color: tokens.colorPaletteRedForeground1 }}
                                />
                            </div>
                        </div>

                        {/* Question Configuration */}
                        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '16px', marginBottom: '16px' }}>
                            <Field label="Question Text" required>
                                <Textarea
                                    value={question.text}
                                    onChange={(_, data) => updateQuestion(questionIndex, 'text', data.value)}
                                    placeholder="Enter your question..."
                                    rows={2}
                                />
                            </Field>

                            <Field label="Question Type">
                                <Dropdown
                                    value={questionTypeOptions.find(opt => opt.value === question.type)?.label}
                                    selectedOptions={[question.type]}
                                    onOptionSelect={(_, data) => {
                                        if (data.optionValue) {
                                            updateQuestion(questionIndex, 'type', data.optionValue as QuestionType);
                                            // Reset type-specific fields
                                            updateQuestion(questionIndex, 'answerOptions', undefined);
                                            updateQuestion(questionIndex, 'maxLength', undefined);
                                        }
                                    }}
                                >
                                    {questionTypeOptions.map(option => (
                                        <Option key={option.value} value={option.value}>
                                            {option.label}
                                        </Option>
                                    ))}
                                </Dropdown>
                            </Field>
                        </div>

                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
                            <Switch
                                checked={question.isMandatory}
                                onChange={(_, data) => updateQuestion(questionIndex, 'isMandatory', data.checked)}
                            />
                            <Text>Required question</Text>
                        </div>

                        {/* Type-specific configurations */}
                        {needsAnswerOptions(question.type) && (
                            <div style={{ marginBottom: '16px' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                                    <Text weight="semibold">Answer Options</Text>
                                    <Button
                                        size="small"
                                        appearance="secondary"
                                        icon={<Add24Regular />}
                                        onClick={() => addAnswerOption(questionIndex)}
                                    >
                                        Add Option
                                    </Button>
                                </div>
                                
                                {(question.answerOptions || []).map((option, optionIndex) => (
                                    <div key={optionIndex} style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                                        <Input
                                            value={option}
                                            onChange={(_, data) => updateAnswerOption(questionIndex, optionIndex, data.value)}
                                            placeholder={`Option ${optionIndex + 1}`}
                                            style={{ flex: 1 }}
                                        />
                                        <Button
                                            size="small"
                                            appearance="subtle"
                                            icon={<Delete24Regular />}
                                            onClick={() => deleteAnswerOption(questionIndex, optionIndex)}
                                            style={{ color: tokens.colorPaletteRedForeground1 }}
                                        />
                                    </div>
                                ))}
                                
                                {(question.answerOptions || []).length === 0 && (
                                    <Text size={300} style={{ color: tokens.colorNeutralForeground2 }}>
                                        Click "Add Option" to create answer choices
                                    </Text>
                                )}
                            </div>
                        )}

                        {needsMaxLength(question.type) && (
                            <Field label="Maximum Character Length (Optional)" style={{ marginBottom: '16px' }}>
                                <Input
                                    type="number"
                                    value={question.maxLength?.toString() || ''}
                                    onChange={(_, data) => updateQuestion(questionIndex, 'maxLength', data.value ? parseInt(data.value) : undefined)}
                                    placeholder="e.g., 500"
                                />
                            </Field>
                        )}

                        <Divider />
                        <Text size={300} style={{ color: tokens.colorNeutralForeground2, marginTop: '8px' }}>
                            {questionTypeOptions.find(opt => opt.value === question.type)?.description}
                        </Text>
                    </Card>
                ))}

                {/* Add Question Button - placed at the end of questions list */}
                <div style={{ 
                    display: 'flex', 
                    justifyContent: 'center', 
                    marginTop: survey.questions.length > 0 ? '20px' : '16px' 
                }}>
                    <Button
                        appearance="primary"
                        icon={<Add24Regular />}
                        onClick={addQuestion}
                        size="large"
                    >
                        Add Question
                    </Button>
                </div>
            </Card>

            {/* Action Buttons - placed at the bottom for better UX flow */}
            <div style={{ 
                display: 'flex', 
                justifyContent: 'center', 
                gap: '12px', 
                marginTop: '32px',
                padding: '20px',
                borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
                backgroundColor: tokens.colorNeutralBackground1,
                position: 'sticky',
                bottom: 0,
                zIndex: 10
            }}>
                <Button
                    appearance="primary"
                    icon={<Save24Regular />}
                    onClick={handleSave}
                    disabled={isSaving || !survey.title.trim()}
                    size="large"
                >
                    {isSaving ? 'Saving...' : 'Save Survey'}
                </Button>
                <Button
                    appearance="secondary"
                    onClick={onCancel}
                    size="large"
                >
                    Cancel
                </Button>
            </div>
        </div>
    );
};

export default VisualSurveyEditor;
